import * as React from 'react';
import { useAtomValue } from 'jotai';
import Image from 'next/image';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { Rating } from '../legacy';
import { Badge } from '@restworld/ui-ds-base';
import { RATING_RANGE } from '@restworld/utility-types';
import { formatGoogleOpeningHours, getAssetSrc, customImageLoader } from '@restworld/utils-common';

import { restaurantDetailsAtom, ShowcaseSectionTitle } from '.';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IVenueAboutUsSectionProps {}

interface OpeningHour {
  day: string;
  times: string[];
}

const OpeningHours: React.FC<{ openingHours?: string[] }> = ({ openingHours }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { t } = useTranslation('restaurant-showcase-v2');
  const { t: tC } = useTranslation('common');

  const formattedHours = React.useMemo<OpeningHour[]>(
    () =>
      (openingHours || []).map((hour) => {
        const [day, times] = hour.split(': ');
        return {
          day,
          times: times ? times.split('|') : ['closed'],
        };
      }),
    [openingHours]
  );

  const formattedOpeningHoursList = React.useMemo(
    () =>
      formattedHours.map((hour, idx) => (
        <div
          key={hour.day}
          className={`self-stretch ${
            idx !== 0 ? 'pt-2 border-t border-neutral-40' : 'pt-1'
          } inline-flex justify-between items-start`}
        >
          <h5 className="justify-start text-neutral-200 text-sm font-normal">{tC(hour.day)}</h5>
          <div className={`inline-flex flex-col justify-center items-end${hour.times.length > 1 ? ' gap-1' : ''}`}>
            {hour.times.map((time, tIdx) => (
              <h5 key={tIdx} className="text-right justify-start text-neutral-700 text-sm font-medium">
                {time}
              </h5>
            ))}
          </div>
        </div>
      )),
    [formattedHours, tC]
  );

  if (!isOpen)
    return (
      <a
        href="#!"
        className="self-stretch p-4 rounded-lg inline-flex justify-between items-center border border-neutral-90"
        onClick={() => setIsOpen(true)}
      >
        <span className="flex-1 flex justify-between items-center">
          <h5 className="justify-start text-base font-normal">{t('opening_hours')}</h5>
          <ChevronDownIcon className="w-6 h-6 text-neutral-700" />
        </span>
      </a>
    );

  return (
    <div className="w-full min-w-[20rem] p-4 rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-90 inline-flex flex-col justify-start items-start gap-4 transition-all duration-300 ease-in-out">
      <a href="#!" className="w-full inline-flex justify-between items-center" onClick={() => setIsOpen(false)}>
        <h5 className="justify-start text-neutral-700 text-base font-normal">{t('opening_hours')}</h5>
        <ChevronUpIcon className="w-6 h-6 text-neutral-700" />
      </a>
      {formattedOpeningHoursList}
    </div>
  );
};

export const VenueAboutUsSection: React.FC<IVenueAboutUsSectionProps> = () => {
  const { t } = useTranslation('restaurant-showcase-v2');
  const restaurantDetails = useAtomValue(restaurantDetailsAtom);

  const openingHours = React.useMemo(
    () => formatGoogleOpeningHours(restaurantDetails?.openingHours || []),
    [restaurantDetails?.openingHours]
  );
  if (
    !restaurantDetails?.briefDescription &&
    !restaurantDetails?.shifts?.length &&
    !restaurantDetails?.covers &&
    !restaurantDetails?.staffSize &&
    !openingHours.length
  )
    return <></>;

  return (
    <div className="self-stretch inline-flex flex-col justify-start items-start gap-4">
      <ShowcaseSectionTitle title={t('about_us')} />
      {!!restaurantDetails?.rating && (
        <div className="inline-flex justify-start items-center gap-2.5">
          <Image
            src={getAssetSrc('/utils-assets/google-icon.svg')}
            width={47}
            height={16}
            alt="google"
            loader={customImageLoader}
          />
          <Rating rating={+restaurantDetails?.rating as RATING_RANGE} size="size" className="text-orange-500" />
          <h5 className="justify-start text-neutral-700 text-sm font-semibold">{restaurantDetails?.rating}/5</h5>
          {!!restaurantDetails?.totalReviews && (
            <h5 className="justify-start text-neutral-700 text-sm font-semibold">
              ({restaurantDetails?.totalReviews} {t('reviews')})
            </h5>
          )}
        </div>
      )}
      {!!restaurantDetails?.briefDescription && (
        <h5 className="self-stretch justify-start text-base font-normal">{restaurantDetails.briefDescription}</h5>
      )}
      <div className="self-stretch inline-flex justify-start items-start gap-4 flex-wrap content-start">
        {restaurantDetails?.shifts?.length
          ? restaurantDetails?.shifts?.map((shift, idx) => (
              <Badge key={idx} variant="neutral">
                {shift}
              </Badge>
            ))
          : null}
        {!!restaurantDetails?.covers && (
          <Badge variant="neutral">
            {t('covers')}: {restaurantDetails.covers}
          </Badge>
        )}
        {!!restaurantDetails?.staffSize && (
          <Badge variant="neutral">
            {t('people_in_the_staff')}: {restaurantDetails.staffSize}
          </Badge>
        )}
      </div>
      {openingHours?.length > 0 && <OpeningHours openingHours={openingHours} />}
    </div>
  );
};

export default VenueAboutUsSection;
